"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import Ditto1 from "@/assets/ditto-1.svg";
import Ditto2 from "@/assets/ditto-2.svg";
import Image from "next/image";
import noise from "@/assets/noise.png";
import HubForHealthcareEducationSectionLeftShape from "@/assets/HubForHealthcareEducationSectionLeftShape.svg";
import HubForHealthcareEducationSectionRightShape from "@/assets/HubForHealthcareEducationSectionRightShape.svg";
import { motion } from "framer-motion";
import { MotionPrismicNextImage } from "@/components/ui/motion-prismic-next-image";
import { PrismicNextImage } from "@prismicio/next";

/**
 * Props for `HubForHealthcareEducationSection`.
 */
export type HubForHealthcareEducationSectionProps =
  SliceComponentProps<Content.HubForHealthcareEducationSectionSlice>;

/**
 * Component for "HubForHealthcareEducationSection" Slices.
 */
const HubForHealthcareEducationSection: FC<
  HubForHealthcareEducationSectionProps
> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#F8FCFF] py-20 relative"
    >
      <Bounded as="div" className="container mx-auto">
        <div className="flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <PrismicRichText
              field={slice.primary.title}
              components={{
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                  return <span className="text-primary">{children}</span>;
                },
                heading1: ({ children }) => (
                  <h1 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h1>
                ),
                heading2: ({ children }) => (
                  <h2 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h3>
                ),
                heading4: ({ children }) => (
                  <h4 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h4>
                ),
                heading5: ({ children }) => (
                  <h5 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h5>
                ),
                heading6: ({ children }) => (
                  <h6 className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4">
                    {children}
                  </h6>
                ),
              }}
            />
          </motion.div>
          <motion.div
            className="font-merriweather font-normal text-xl leading-8 text-left text-[#033D61] mb-8 max-w-[883px]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <PrismicRichText field={slice.primary.top_description} />
          </motion.div>
          <motion.div className="grid grid-cols-2 xl:grid-cols-4 items-center justify-center gap-8 mb-8 z-2" initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 1 }}>
            {slice.primary.images.map((imageContent, index) => (
              <PrismicNextImage
                key={index}
                field={imageContent.image}
                className="rounded-[14px] border-[5px] border-white w-[255px] h-[255px]"
              />
            ))}
          </motion.div>
          <motion.div
            className="relative bg-[#7B92A145] rounded-[12px] z-2 p-6 text-[#EBF7FF] font-merriweather text-xl font-normal leading-8 max-w-[593px] w-full mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <Image
              src={Ditto1.src}
              alt="Ditto 1"
              width={49}
              height={36}
              className="absolute top-2 left-2 pointer-events-none select-none"
            />
            <Image
              src={Ditto2.src}
              alt="Ditto 2"
              width={49}
              height={36}
              className="absolute bottom-2 right-2 pointer-events-none select-none"
            />
            {slice.primary.quote}
          </motion.div>
          <motion.div
            className="font-merriweather font-normal text-xl leading-8 z-2 text-left text-[#EBF7FF] max-w-[924px]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <PrismicRichText field={slice.primary.bottom_description} />
          </motion.div>
        </div>
      </Bounded>
      <div className="absolute bottom-0 w-full h-[867px] md:h-[785px] lg:h-[737px] xl:h-[440px] overflow-hidden">
        <div className="relative bg-[#045B91] h-full w-full">
          <div
            className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
            style={{
              background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
            }}
          />
          <Image
            src={HubForHealthcareEducationSectionLeftShape.src}
            alt="left shape"
            width={612}
            height={568}
            className="absolute left-[-50%] sm:left-[-20%] md:left-0 top-0 h-[640px] w-auto pointer-events-none select-none"
          />
          <Image
            src={HubForHealthcareEducationSectionRightShape.src}
            alt="right shape"
            width={617}
            height={573}
            className="absolute right-[-50%] sm:right-[-20%] md:right-0 bottom-0 h-[640px] w-auto pointer-events-none select-none"
          />
        </div>
      </div>
    </section>
  );
};

export default HubForHealthcareEducationSection;
